import styled, { x } from '@xstyled/styled-components';
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { POPUP } from '..';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import ReactDOM from 'react-dom';
import { Card } from '~/ui/card/Card';
import { usePopup } from '~/training/sequence/templates/items/audio-popup/components/usePopup';
import closeIcon from '~/assets/icons/close.svg';
import { t } from 'ttag';
import { drillAnimation } from '~/ui/animation';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { TickMarkIcon } from '../audio-popup/components/TickMarkIcon';
import { HotspotPlusIcon } from '../audio-popup/components/HotspotPlusIcon';
import { ImageMapPopupLayoutContext } from '../image-map/ImageMapPopupLayoutContext';
import { getImageMapBoundsEl } from '../image-map/imageMapBounds';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { stripHtml } from '~/lib/sanitization';
import {
  centerPopupInViewport,
  clampPopupLeftToViewport,
  shouldCenterPopupForReflow
} from './popupViewportLayout';

interface Props {
  id?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  top?: number;
  left?: number;
  btnTitle?: string;
  metric?: string;
  isPopupCentered?: boolean;
  /**
   * Fixed popup below the hotspot (anchored to the button).
   * Ignored when `isPopupCentered` is true — centering wins (e.g. Interactive4 on mobile).
   */
  openPopupUnderButton?: boolean;
  /** Optional px added to fixed `left` after anchor math (overrides ImageMap context; default 100). */
  popupFixedOffsetLeftPx?: number;
  /** Optional px added to fixed `top` after anchor math (overrides ImageMap context; default 20). */
  popupFixedOffsetTopPx?: number;
  blocking?: boolean;
  btnSize?: number;
}

interface DrillBoxProps {
  $btnSize?: number;
  $drillBg?: string;
  $drillOpacity?: number;
}

const CloseIcon = styled(closeIcon)`
  transform: rotate(180deg);
  color: ${p => p.theme.colors.black};
`;

const CloseButton = styled.button`
  position: absolute;
  z-index: 1;
  border: none;
  padding: 0;
  background: transparent;
  right: 20px;
`;

const DrillBoxAnimation = styled.div<DrillBoxProps>`
  width: ${p => `${p.$btnSize ?? 84}px`};
  height: ${p => `${p.$btnSize ?? 84}px`};
  animation: ${drillAnimation} 2.3s ease infinite;
  position: absolute;
  /* inset + margin centers without transform (drillAnimation owns transform;
     odd sizes + uncentered absolute look shifted in Safari). */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  background-color: ${p => p.$drillBg ?? p.theme.colors.accentDark};
  border-radius: 50%;
  opacity: ${p => p.$drillOpacity ?? 0.5};
  z-index: 1;
`;

const seqSelector = (s: SequenceStore) =>
  select(s, 'addBlocker', 'removeBlocker');

const PopupButton = ({
  id,
  title,
  subtitle,
  body,
  btnTitle = '+',
  isPopupCentered,
  openPopupUnderButton = false,
  popupFixedOffsetLeftPx: itemOffsetLeft,
  popupFixedOffsetTopPx: itemOffsetTop,
  blocking = false,
  btnSize = 64
}: Props) => {
  const { open, setOpen, popupRef } = usePopup();
  const ref = useRef<HTMLDivElement>();
  const openerButtonRef = useRef<HTMLButtonElement>(null);
  const [status, setStatus] = useState<string>('inprogress');
  const [top, setTop] = useState<number>();
  const [left, setLeft] = useState<number>();
  const { addBlocker, removeBlocker } = useSequenceStore(seqSelector, shallow);
  const imageMapPopupLayout = useContext(ImageMapPopupLayoutContext);
  /** Used only when not centered — legacy anchored popup nudge (+100/+20). */
  const anchorOffsetLeft =
    itemOffsetLeft ?? imageMapPopupLayout?.popupFixedOffsetLeftPx ?? 100;
  const anchorOffsetTop =
    itemOffsetTop ?? imageMapPopupLayout?.popupFixedOffsetTopPx ?? 20;
  /** CAT-1228 / WCAG 1.4.10: center at ≤320 CSS px (400% zoom), ignore legacy offsets. */
  const centerInViewport =
    Boolean(isPopupCentered) || shouldCenterPopupForReflow();

  // CAT-1217 / WCAG 2.4.11: keep Tab inside the open popup so focus cannot
  // land on hotspots fully obscured behind it (e.g. Shift+Tab → Play audio).
  useFocusTrap(popupRef, open, undefined, openerButtonRef);

  // CAT-1245 / WCAG 2.4.6: name the control by popup topic, not "Open …".
  const openerLabel =
    stripHtml(title || subtitle || '').trim() ||
    (btnTitle && btnTitle !== '+' ? String(btnTitle) : '') ||
    t`Open details`;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  useEffect(() => {
    if (blocking && id) {
      addBlocker(id);
    }

    return () => {
      if (blocking && id) {
        removeBlocker(id);
      }
    };
  }, []);

  useEffect(() => {
    if (!blocking) return;

    if (open && id) {
      removeBlocker(id);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setStatus('completed');

      const handler = () => {
        setOpen(false);
      };

      setTimeout(() => {
        document.addEventListener('click', handler);
      });

      return () => document.removeEventListener('click', handler);
    }
  }, [open, popupRef]);

  useLayoutEffect(() => {
    if (!open) return;

    let attempts = 0;
    const maxAttempts = 15;

    function updatePosition() {
      const rect = ref?.current?.getBoundingClientRect();
      const popupEl = popupRef?.current;
      const popupRect = popupEl?.getBoundingClientRect();
      if (!rect || !popupEl) return;

      const popupReady =
        popupRect && popupRect.width >= 8 && popupRect.height >= 8;

      if (!popupReady && attempts++ < maxAttempts) {
        requestAnimationFrame(updatePosition);
        return;
      }

      if (!popupRect || popupRect.width < 4) return;

      const anchoredBelowHotspot = () => {
        let nextLeft = rect.left + rect.width / 2 - popupRect.width / 2;
        const shouldClamp =
          openPopupUnderButton || imageMapPopupLayout?.clampPopupToImageMap;
        if (shouldClamp) {
          const mapEl = getImageMapBoundsEl(ref.current ?? undefined);
          const mapRect = mapEl?.getBoundingClientRect();
          if (mapRect) {
            const pad = 8;
            nextLeft = Math.max(
              mapRect.left + pad,
              Math.min(mapRect.right - popupRect.width - pad, nextLeft)
            );
          }
        }
        // Always keep the final on-screen position inside the viewport
        // (legacy +100 offset is applied at render when not centered).
        nextLeft = clampPopupLeftToViewport(
          nextLeft,
          popupRect.width,
          anchorOffsetLeft
        );
        let nextTop = rect.bottom;
        const padY = 8;
        const vh = window.innerHeight;
        const finalTop = nextTop + anchorOffsetTop;
        if (finalTop + popupRect.height > vh - padY) {
          nextTop = Math.max(
            padY - anchorOffsetTop,
            vh - popupRect.height - padY - anchorOffsetTop
          );
        }
        setTop(nextTop);
        setLeft(nextLeft);
      };

      // CAT-1228: ≤320 CSS px (DevTools or ~400% page zoom) → center in viewport.
      if (isPopupCentered || shouldCenterPopupForReflow()) {
        const { left: nextLeft, top: nextTop } = centerPopupInViewport(
          popupRect.width,
          popupRect.height
        );
        setTop(nextTop);
        setLeft(nextLeft);
        return;
      }

      if (openPopupUnderButton) {
        anchoredBelowHotspot();
        return;
      }

      anchoredBelowHotspot();
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [
    open,
    openPopupUnderButton,
    isPopupCentered,
    imageMapPopupLayout,
    anchorOffsetLeft,
    anchorOffsetTop
  ]);

  const clickHandler = () => {
    setOpen(!open);
  };

  return (
    <x.div
      w={`${btnSize}px`}
      h={`${btnSize}px`}
      backgroundColor="rgba(255,255,255,0.2)"
      borderRadius="50%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      position="relative"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
    >
      <DrillBoxAnimation $btnSize={btnSize - 4} $drillOpacity={1} />

      <x.button
        ref={openerButtonRef}
        onClick={clickHandler}
        backgroundColor="white"
        color="black"
        borderRadius="50%"
        w={`${btnSize - 10}px`}
        h={`${btnSize - 10}px`}
        display="flex"
        justifyContent="center"
        alignItems="center"
        overflow="hidden"
        aria-label={openerLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        zIndex="2"
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        margin="auto"
        style={{ boxSizing: 'border-box', flexShrink: 0 }}
      >
        {status === 'completed' ? (
          <TickMarkIcon />
        ) : btnTitle === '+' ? (
          <HotspotPlusIcon aria-hidden />
        ) : (
          <x.span
            style={{
              fontSize: '18px',
              fontWeight: '700',
              display: 'block',
              lineHeight: '22px',
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}
          >
            {btnTitle}
          </x.span>
        )}
      </x.button>
      {open &&
        ReactDOM.createPortal(
          <Card
            ref={popupRef}
            role="dialog"
            aria-modal="true"
            aria-label={openerLabel}
            position="fixed"
            p="md"
            onClick={e => e.stopPropagation()}
            top={`${(top ?? 0) + (centerInViewport ? 0 : anchorOffsetTop)}px`}
            left={`${(left ?? 0) + (centerInViewport ? 0 : anchorOffsetLeft)}px`}
            gap={''}
            tabIndex={-1}
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: 'min(360px, calc(100vw - 32px))',
              maxHeight: '80vh'
            }}
          >
            <CloseButton
              onClick={() => setOpen(false)}
              aria-label={t`close`}
              role="button"
            >
              <CloseIcon />
            </CloseButton>
            <div
              role="alert"
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                flex: '1 1 auto'
              }}
            >
              <x.div
                color="accentDark"
                className="subtitleStyleForDarkTheme"
                style={{ wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: subtitle || '' }}
              />
              <x.h2
                p="15px 0px 15px 0px"
                style={{ wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: title || '' }}
              />
              <x.div
                style={{
                  overflowY: 'auto',
                  minHeight: 0,
                  flex: '1 1 auto',
                  // Smooth momentum scrolling on iOS Safari; no-op
                  // elsewhere.
                  WebkitOverflowScrolling: 'touch',
                  wordBreak: 'break-word'
                }}
                dangerouslySetInnerHTML={{ __html: body || '' }}
              />
            </div>
          </Card>,
          document.getElementById('root')!
        )}
    </x.div>
  );
};

export type PopupDefinition = ItemDefinition<typeof POPUP, Props>;

export class Popup extends Item<typeof POPUP, Props> {
  protected render({
    id,
    title,
    subtitle,
    body,
    top,
    left,
    btnTitle,
    metric = 'px',
    isPopupCentered,
    openPopupUnderButton,
    popupFixedOffsetLeftPx,
    popupFixedOffsetTopPx,
    blocking = false,
    btnSize
  }: RenderProps<Props>) {
    return top && left ? (
      <x.div
        position="absolute"
        top={`${top}${metric}`}
        left={`${left}${metric}`}
        style={{ transform: 'translate(-50%, -50%)' }}
      >
        <PopupButton
          id={id}
          title={title}
          body={body}
          subtitle={subtitle}
          btnTitle={btnTitle}
          isPopupCentered={isPopupCentered}
          openPopupUnderButton={openPopupUnderButton}
          popupFixedOffsetLeftPx={popupFixedOffsetLeftPx}
          popupFixedOffsetTopPx={popupFixedOffsetTopPx}
          blocking={blocking}
          btnSize={btnSize}
        />
      </x.div>
    ) : (
      <PopupButton
        id={id}
        title={title}
        body={body}
        subtitle={subtitle}
        btnTitle={btnTitle}
        isPopupCentered={isPopupCentered}
        openPopupUnderButton={openPopupUnderButton}
        popupFixedOffsetLeftPx={popupFixedOffsetLeftPx}
        popupFixedOffsetTopPx={popupFixedOffsetTopPx}
        blocking={blocking}
        btnSize={btnSize}
      />
    );
  }
}

registry[POPUP] = Popup;
